import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SiteHeader, PBOLogo } from "@/components/Logo";
import { fetchContest, joinContest } from "@/lib/contestApi";
import { saveContestSession, getContestSession } from "@/lib/contestStorage";
import { claimContestSeat } from "@/lib/authApi";
import useAuth from "@/hooks/useAuth";

export default function JoinContest() {
  const { code: codeParam } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [code, setCode] = useState((codeParam || "").toUpperCase());
  const [username, setUsername] = useState("");
  const [contest, setContest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [claiming, setClaiming] = useState(null);

  useEffect(() => {
    if (code.length >= 4) {
      fetchContest(code)
        .then((c) => setContest(c))
        .catch(() => setContest(null));
    } else {
      setContest(null);
    }
  }, [code]);

  // If we already have a session for this code, jump in directly
  useEffect(() => {
    if (codeParam) {
      const s = getContestSession(codeParam);
      if (s) nav(`/contest/${codeParam.toUpperCase()}`, { replace: true });
    }
  }, [codeParam, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.error("Pick a display name");
      return;
    }
    if (code.length < 4) {
      toast.error("Enter the 4-letter contest code");
      return;
    }
    setBusy(true);
    try {
      const res = await joinContest(code, username.trim());
      saveContestSession(code.toUpperCase(), res.participant_id, res.username);
      toast.success(`Joined ${code.toUpperCase()}`);
      nav(`/contest/${code.toUpperCase()}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join contest");
    } finally {
      setBusy(false);
    }
  };

  const doClaim = async (m) => {
    setClaiming(m.id);
    try {
      const res = await claimContestSeat(code.toUpperCase(), m.id);
      saveContestSession(code.toUpperCase(), res.participant_id, res.username);
      toast.success(`Welcome back, ${res.username}`);
      nav(`/contest/${code.toUpperCase()}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't claim that seat");
    } finally {
      setClaiming(null);
    }
  };

  const unclaimedSeats = (contest?.members || []).filter((m) => !m.has_account);

  return (
    <div className="min-h-screen bg-pbo-cream">
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid sm:grid-cols-[auto_1fr] gap-6 items-start mb-10">
          <PBOLogo size={96} />
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-pbo-gold">
              Join a contest
            </div>
            <h1 className="font-serif text-pbo-green text-4xl sm:text-5xl mt-2 leading-tight">
              Pick your four, stay under cap.
            </h1>
            <p className="text-pbo-ink/65 mt-3 text-sm">
              {user
                ? `Signed in as ${user.email}. Enter the contest code and pick a display name.`
                : "Enter the contest code and pick a display name."}
            </p>
          </div>
        </div>

        {/* Claim existing seat panel (auth users, contest exists with unclaimed rows) */}
        {user && contest && unclaimedSeats.length > 0 && (
          <div
            className="bg-pbo-green/5 border border-pbo-green/30 rounded-2xl p-5 mb-6"
            data-testid="claim-panel"
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-pbo-green/15 text-pbo-green grid place-items-center shrink-0">
                <UserCheck className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-serif text-pbo-green text-lg leading-tight mb-1">
                  Already in this contest?
                </h3>
                <p className="text-pbo-ink/70 text-xs mb-3 leading-relaxed">
                  Tap your name to attach it to <strong>{user.email}</strong>{" "}
                  and skip re-entering picks. This preserves anything you
                  already submitted.
                </p>
                <div className="flex flex-wrap gap-2">
                  {unclaimedSeats.map((m) => (
                    <Button
                      key={m.id}
                      variant="outline"
                      onClick={() => doClaim(m)}
                      disabled={claiming === m.id}
                      className="border-pbo-green text-pbo-green hover:bg-pbo-green hover:text-pbo-cream rounded-full h-9"
                      data-testid={`claim-seat-${m.id}`}
                    >
                      {claiming === m.id ? "Claiming…" : m.username}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <form
          onSubmit={onSubmit}
          className="bg-white border border-pbo-border rounded-2xl p-6 sm:p-8 space-y-6"
        >
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-pbo-muted">
              Contest code
            </Label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
              maxLength={6}
              placeholder="ABCD"
              className="font-mono text-3xl tracking-[0.4em] h-16 text-center text-pbo-green bg-pbo-paper"
              data-testid="join-contest-code-input"
            />
            {code.length >= 4 && (
              <div className="text-xs mt-1">
                {contest ? (
                  <span className="text-pbo-green-soft">
                    ✓ Contest: <strong>{contest.name}</strong> · cap{" "}
                    <strong>${contest.salary_cap.toLocaleString()}</strong> ·{" "}
                    {contest.members.length} in the room ·{" "}
                    {contest.status === "open" ? "Picks open" : "Locked"}
                  </span>
                ) : (
                  <span className="text-pbo-muted">Looking up code…</span>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-pbo-muted">
              Your display name
            </Label>
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value.slice(0, 30))}
              placeholder="e.g. Eli"
              className="h-12 bg-pbo-paper"
              data-testid="join-contest-username-input"
            />
            <p className="text-[11px] text-pbo-muted">
              This name shows on the scoreboard. First time joining? Just type
              it fresh.
            </p>
          </div>

          <Button
            type="submit"
            disabled={busy}
            className="w-full h-14 rounded-full bg-pbo-green text-pbo-cream hover:bg-pbo-green-deep font-semibold text-base"
            data-testid="submit-join-contest-btn"
          >
            {busy ? "Joining..." : "Enter contest"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </form>
      </div>
    </div>
  );
}
